import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { AutomationRule } from '@/lib/integrations/supabase/types';

/**
 * Get all automation rules for a business.
 */
export async function getAutomationRules(businessId: string): Promise<AutomationRule[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('automation_rules')
    .select('*')
    .eq('business_id', businessId)
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Toggle an automation rule.
 * Verifies the rule belongs to the business before updating.
 */
export async function toggleAutomationRule(
  ruleId: string,
  enabled: boolean,
  businessId: string
): Promise<void> {
  const supabase = getBrowserClient();

  const { data: rule, error: fetchError } = await supabase
    .from('automation_rules')
    .select('id, business_id')
    .eq('id', ruleId)
    .eq('business_id', businessId)
    .single();

  if (fetchError || !rule) {
    throw new Error('Automation rule not found or access denied');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase
    .from('automation_rules') as any)
    .update({ enabled })
    .eq('id', ruleId)
    .eq('business_id', businessId);

  if (error) {
    throw error;
  }
}

/**
 * Create default automation rules for a new business.
 * Only creates rules that don't already exist (by type).
 */
export async function createDefaultAutomationRules(businessId: string): Promise<void> {
  const supabase = getBrowserClient();

  const { data: existingRules } = await supabase
    .from('automation_rules')
    .select('type')
    .eq('business_id', businessId);

  const existingTypes = new Set((existingRules || []).map((r: any) => r.type).filter(Boolean));

  const defaultRules = [
    {
      business_id: businessId,
      name: 'Weekly Business Summary',
      description: 'Receive weekly email with business performance, top staff, revenue, and insights',
      type: 'weekly_summary',
      enabled: true,
      config: {
        day_of_week: 6,
        hour_utc: 5,
      },
    },
    {
      business_id: businessId,
      name: 'Item Deleted Notification',
      description: 'Send email when an inventory item is deleted',
      type: 'email_item_deleted',
      enabled: true,
      config: {},
    },
    {
      business_id: businessId,
      name: 'Out of Stock Alert',
      description: 'Send email when an item reaches zero quantity',
      type: 'email_out_of_stock',
      enabled: true,
      config: {},
    },
    {
      business_id: businessId,
      name: 'Low Stock Alert',
      description: 'Send email when an item quantity drops below threshold',
      type: 'email_low_stock',
      enabled: true,
      config: {},
    },
    {
      business_id: businessId,
      name: 'Customer Follow-up',
      description: 'Send thank you email with Google review link after payment',
      type: 'customer_followup',
      enabled: true,
      config: {},
    },
    {
      business_id: businessId,
      name: 'Reactivation Campaign',
      description: 'Send "We miss you" email to inactive customers (30+ days)',
      type: 'reactivation_campaign',
      enabled: true,
      config: { inactive_days: 30 },
    },
  ];

  const rulesToInsert = defaultRules.filter(rule => !existingTypes.has(rule.type));

  if (rulesToInsert.length === 0) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase
    .from('automation_rules') as any)
    .insert(rulesToInsert);

  if (error) {
    throw error;
  }
}

/**
 * Update an automation rule's config.
 * Verifies the rule belongs to the business before updating.
 */
export async function updateAutomationConfig(
  ruleId: string,
  config: Record<string, unknown>,
  businessId: string
): Promise<void> {
  const supabase = getBrowserClient();

  const { data: rule, error: fetchError } = await supabase
    .from('automation_rules')
    .select('id, business_id, config')
    .eq('id', ruleId)
    .eq('business_id', businessId)
    .single() as { data: { id: string; business_id: string; config: Record<string, unknown> } | null; error: unknown };

  if (fetchError || !rule) {
    throw new Error('Automation rule not found or access denied');
  }

  const mergedConfig = { ...(rule.config || {}), ...config };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase
    .from('automation_rules') as any)
    .update({ config: mergedConfig })
    .eq('id', ruleId)
    .eq('business_id', businessId);

  if (error) {
    throw error;
  }
}
